import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useLocaleFmt } from "@/lib/i18n/format";
import { adminSetPlan, adminAssignRole, adminRevokeRole } from "@/lib/admin.functions";
import { PageHeader, PageBody } from "@/components/app/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Shield, Users, Briefcase, FileText, Search, Trash2, Crown, AlertTriangle, Webhook, Boxes, ScrollText, RefreshCw, CreditCard, LifeBuoy } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/admin")({
  beforeLoad: async () => {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) throw redirect({ to: "/login" });
    const { data: roles } = await supabase
      .from("user_roles").select("role").eq("user_id", u.user.id);
    if (!roles?.some((r) => r.role === "admin")) {
      throw redirect({ to: "/app" });
    }
  },
  component: AdminPage,
});

const ROLES = ["admin", "partner", "associate", "collaborator", "paralegal", "secretary"] as const;
type Role = typeof ROLES[number];

type ProfileRow = {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  firm_name: string | null;
  plan: string | null;
  ai_queries_used: number | null;
  created_at: string;
  roles?: Role[];
};

function AdminPage() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<ProfileRow[]>([]);
  const [matters, setMatters] = useState<any[]>([]);
  const [analyses, setAnalyses] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const setPlanFn = useServerFn(adminSetPlan);
  const assignRoleFn = useServerFn(adminAssignRole);
  const revokeRoleFn = useServerFn(adminRevokeRole);

  const load = async () => {
    setLoading(true);
    const [{ data: profiles }, { data: roles }, { data: m }, { data: a }, { data: d }] = await Promise.all([
      supabase.from("profiles").select("*").order("created_at", { ascending: false }),
      supabase.from("user_roles").select("user_id, role"),
      supabase.from("matters").select("id, name, status, owner_id, created_at").order("created_at", { ascending: false }).limit(100),
      supabase.from("document_analyses").select("id, source_name, owner_id, created_at").order("created_at", { ascending: false }).limit(100),
      supabase.from("drafts").select("id, title, owner_id, created_at").order("created_at", { ascending: false }).limit(100),
    ]);
    const map = new Map<string, Role[]>();
    (roles ?? []).forEach((r: any) => {
      const arr = map.get(r.user_id) ?? [];
      arr.push(r.role);
      map.set(r.user_id, arr);
    });
    setUsers((profiles ?? []).map((p: any) => ({ ...p, roles: map.get(p.id) ?? [] })));
    setMatters(m ?? []);
    setAnalyses(a ?? []);
    setDrafts(d ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setPlan = async (userId: string, plan: string) => {
    try {
      const res = await setPlanFn({ data: { userId, plan: plan as any } });
      if (!res.ok) return toast.error(res.error ?? t("pages.admin.genericError"));
      toast.success(t("pages.admin.planSet", { plan }));
      load();
    } catch (e: any) { toast.error(e?.message ?? t("pages.admin.genericError")); }
  };

  const grant = async (userId: string, role: Role) => {
    try {
      const res = await assignRoleFn({ data: { userId, role: role as any } });
      if (!res.ok) return toast.error(res.error ?? t("pages.admin.genericError"));
      toast.success(t("pages.admin.roleAssigned", { role }));
      load();
    } catch (e: any) { toast.error(e?.message ?? t("pages.admin.genericError")); }
  };
  const revoke = async (userId: string, role: Role) => {
    try {
      const res = await revokeRoleFn({ data: { userId, role: role as any } });
      if (!res.ok) return toast.error(res.error ?? t("pages.admin.genericError"));
      toast.success(t("pages.admin.roleRemoved", { role }));
      load();
    } catch (e: any) { toast.error(e?.message ?? t("pages.admin.genericError")); }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (u.email ?? "").toLowerCase().includes(s)
      || (u.first_name ?? "").toLowerCase().includes(s)
      || (u.last_name ?? "").toLowerCase().includes(s)
      || (u.firm_name ?? "").toLowerCase().includes(s);
  });

  const stats = {
    users: users.length,
    admins: users.filter((u) => u.roles?.includes("admin")).length,
    matters: matters.length,
    analyses: analyses.length,
    drafts: drafts.length,
  };

  return (
    <>
      <PageHeader
        title={t("pages.admin.title")}
        description={t("pages.admin.description")}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/automations"><AlertTriangle className="mr-2 h-4 w-4" /> {t("pages.admin.automations")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/errors"><AlertTriangle className="mr-2 h-4 w-4" /> {t("pages.admin.errors")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/modules"><Boxes className="mr-2 h-4 w-4" /> {t("pages.admin.modulesPlans")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/module-sync"><RefreshCw className="mr-2 h-4 w-4" /> {t("pages.admin.moduleSync")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/subscriptions"><CreditCard className="mr-2 h-4 w-4" /> {t("pages.admin.subscriptions")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/support"><LifeBuoy className="mr-2 h-4 w-4" /> {t("pages.admin.support")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/webhooks"><Webhook className="mr-2 h-4 w-4" /> {t("pages.admin.webhooks")}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/app/admin/audit"><ScrollText className="mr-2 h-4 w-4" /> {t("pages.admin.auditLog")}</Link>
            </Button>
            <Badge variant="secondary" className="gap-1"><Shield className="h-3 w-3" /> {t("pages.admin.fullAccess")}</Badge>
          </div>
        }
      />
      <PageBody>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-6">
          {[
            { label: t("pages.admin.stats.users"), value: stats.users, icon: Users },
            { label: t("pages.admin.stats.admins"), value: stats.admins, icon: Crown },
            { label: t("pages.admin.stats.matters"), value: stats.matters, icon: Briefcase },
            { label: t("pages.admin.stats.analyses"), value: stats.analyses, icon: FileText },
            { label: t("pages.admin.stats.drafts"), value: stats.drafts, icon: FileText },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="flex items-center gap-3 pt-6">
                <s.icon className="h-5 w-5 text-muted-foreground" />
                <div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users">{t("pages.admin.tabs.usersRoles")}</TabsTrigger>
            <TabsTrigger value="matters">{t("pages.admin.tabs.matters")}</TabsTrigger>
            <TabsTrigger value="analyses">{t("pages.admin.tabs.analyses")}</TabsTrigger>
            <TabsTrigger value="drafts">{t("pages.admin.tabs.drafts")}</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{t("pages.admin.registeredUsers")}</span>
                  <div className="relative w-72">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pages.admin.search")} className="pl-8" />
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-muted-foreground">{t("pages.admin.loading")}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("pages.admin.cols.user")}</TableHead>
                        <TableHead>{t("pages.admin.cols.firm")}</TableHead>
                        <TableHead>{t("pages.admin.cols.plan")}</TableHead>
                        <TableHead>{t("pages.admin.cols.roles")}</TableHead>
                        <TableHead className="text-right">{t("pages.admin.actions")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="font-medium">{[u.first_name, u.last_name].filter(Boolean).join(" ") || "-"}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </TableCell>
                          <TableCell>{u.firm_name || "-"}</TableCell>
                          <TableCell>
                            <Select value={u.plan || "starter"} onValueChange={(v) => setPlan(u.id, v)}>
                              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="starter">starter</SelectItem>
                                <SelectItem value="pro">pro</SelectItem>
                                <SelectItem value="studio">studio</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {(u.roles ?? []).map((r) => (
                                <Badge
                                  key={r}
                                  variant={r === "admin" ? "default" : "secondary"}
                                  className="cursor-pointer"
                                  onClick={() => revoke(u.id, r)}
                                  title={t("pages.admin.removeRoleHint")}
                                >
                                  {r} ×
                                </Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Select onValueChange={(v) => grant(u.id, v as Role)}>
                              <SelectTrigger className="w-36 ml-auto"><SelectValue placeholder={t("pages.admin.addRole")} /></SelectTrigger>
                              <SelectContent>
                                {ROLES.filter((r) => !u.roles?.includes(r)).map((r) => (
                                  <SelectItem key={r} value={r}>{r}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="matters">
            <DataTable rows={matters} columns={["name", "status"]} onDelete={(id) => del(t, "matters", id, load)} />
          </TabsContent>
          <TabsContent value="analyses">
            <DataTable rows={analyses} columns={["source_name"]} onDelete={(id) => del(t, "document_analyses", id, load)} />
          </TabsContent>
          <TabsContent value="drafts">
            <DataTable rows={drafts} columns={["title"]} onDelete={(id) => del(t, "drafts", id, load)} />
          </TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

async function del(t: (k: string) => string, table: "matters" | "document_analyses" | "drafts", id: string, reload: () => void) {
  if (!confirm(t("pages.admin.deleteConfirm"))) return;
  const { error } = await supabase.from(table).delete().eq("id", id);
  if (error) return toast.error(error.message);
  toast.success(t("pages.admin.deleted"));
  reload();
}

function DataTable({ rows, columns, onDelete }: { rows: any[]; columns: string[]; onDelete: (id: string) => void }) {
  const { t } = useTranslation();
  const { localeTag } = useLocaleFmt();
  return (
    <Card>
      <CardContent className="pt-6">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => <TableHead key={c}>{c}</TableHead>)}
              <TableHead>{t("pages.admin.cols.owner")}</TableHead>
              <TableHead>{t("pages.admin.cols.created")}</TableHead>
              <TableHead className="text-right">{t("pages.admin.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                {columns.map((c) => <TableCell key={c}>{r[c] ?? "-"}</TableCell>)}
                <TableCell className="font-mono text-xs">{r.owner_id?.slice(0, 8)}…</TableCell>
                <TableCell className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString(localeTag)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => onDelete(r.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={columns.length + 3} className="text-center text-sm text-muted-foreground py-8">{t("pages.admin.noData")}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
